'use client';

import { useEffect, useState } from 'react';
import { useCountryDetection } from '@/utils/countryDetection';
import type { LocalRate } from '@/lib/pricing/local-currency';

// One rate request per country per page load, however many prices are on the page.
const rates = new Map<string, Promise<LocalRate | null>>();

function fetchRate(country: string): Promise<LocalRate | null> {
  if (!rates.has(country)) {
    rates.set(
      country,
      fetch(`/api/pricing/exchange-rate?country=${encodeURIComponent(country)}`)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null),
    );
  }
  return rates.get(country)!;
}

/** The visitor's currency and rate, or null while unknown (callers then show USD). */
export function useLocalCurrency(): LocalRate | null {
  const { country, loading } = useCountryDetection();
  const [rate, setRate] = useState<LocalRate | null>(null);
  useEffect(() => {
    if (loading || !country) return;
    if (country.code === 'US') { setRate(null); return; }
    let live = true;
    fetchRate(country.code).then((r) => { if (live) setRate(r); });
    return () => { live = false; };
  }, [country, loading]);
  return rate;
}
